import { useEffect, useState } from "react";
import { AlertTriangle, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ukDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const activeOf = (caseRow) => (caseRow?.pg_insolvencies || []).filter((r) => !r.discharged_at);

export const PgInsolvencyBadge = ({ caseRow, testid }) => {
  const act = activeOf(caseRow);
  if (!act.length) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border bg-red-50 text-red-800 border-red-300 mt-1 mr-1"
      title={act.map((r) => `${r.guarantor_name}: ${r.type_label}${r.case_reference ? ` (${r.case_reference})` : ""} ${ukDate(r.date)}`).join(" · ")}
      data-testid={testid || `pg-insolvency-badge-${caseRow.case_id}`}>
      <AlertTriangle className="w-2.5 h-2.5" /> PG {act.length === 1 ? act[0].insolvency_type.replace("_", " ") : `insolvent ×${act.length}`}
    </span>
  );
};

const LogDialog = ({ caseRow, open, onOpenChange, onSaved }) => {
  const [types, setTypes] = useState([]);
  const guarantors = [caseRow.borrower_director_name || caseRow.director, ...(caseRow.additional_contacts || []).filter((c) => /guarantor|director/i.test(c.role || "")).map((c) => c.name)].filter(Boolean);
  const [form, setForm] = useState({ guarantor_name: guarantors[0] || "", insolvency_type: "bankruptcy", case_reference: "", date: "", practitioner: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/legal/pg-insolvency/types").then((r) => setTypes(r.data.types || [])).catch(() => {}); }, [open]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.guarantor_name.trim() || !form.date) { toast.error("Guarantor name and date are required"); return; }
    setBusy(true);
    try { await api.post(`/legal/pg-insolvency/${caseRow.case_id}`, form); toast.success("Insolvency logged — letters to this guarantor are now blocked"); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(errorDetailMessage(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pg-insolvency-dialog">
        <DialogHeader><DialogTitle>Log guarantor insolvency</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Guarantor</Label>
            <Input list="pg-insolvency-names" value={form.guarantor_name} onChange={(e) => set("guarantor_name", e.target.value)} placeholder="Full name as on the PG" data-testid="pg-insolvency-name" />
            <datalist id="pg-insolvency-names">{guarantors.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Insolvency type</Label>
              <Select value={form.insolvency_type} onValueChange={(v) => set("insolvency_type", v)}>
                <SelectTrigger data-testid="pg-insolvency-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date of order / arrangement</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="pg-insolvency-date" />
            </div>
            <div>
              <Label className="text-xs">Case / court reference</Label>
              <Input value={form.case_reference} onChange={(e) => set("case_reference", e.target.value)} placeholder="e.g. BR-2026-000123" data-testid="pg-insolvency-ref" />
            </div>
            <div>
              <Label className="text-xs">Trustee / IP / supervisor</Label>
              <Input value={form.practitioner} onChange={(e) => set("practitioner", e.target.value)} data-testid="pg-insolvency-ip" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pg-insolvency-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-red-700 hover:bg-red-800" disabled={busy} onClick={save} data-testid="pg-insolvency-save">Log insolvency</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PgInsolvencyBanner = ({ caseRow, onChanged }) => {
  const [open, setOpen] = useState(false);
  const act = activeOf(caseRow);
  const past = (caseRow?.pg_insolvencies || []).filter((r) => r.discharged_at);
  const discharge = async (r) => {
    if (!window.confirm(`Mark ${r.guarantor_name}'s ${r.type_label} as discharged / ended?`)) return;
    try { await api.post(`/legal/pg-insolvency/${caseRow.case_id}/${r.id}/discharge`, { discharged_on: new Date().toISOString().slice(0, 10) }); toast.success("Marked discharged"); onChanged?.(); }
    catch (e) { toast.error(errorDetailMessage(e, "Failed")); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove the insolvency record for ${r.guarantor_name}? Only do this if it was logged in error.`)) return;
    try { await api.delete(`/legal/pg-insolvency/${caseRow.case_id}/${r.id}`); toast.success("Removed"); onChanged?.(); }
    catch (e) { toast.error(errorDetailMessage(e, "Failed")); }
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${act.length ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} data-testid="pg-insolvency-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-sm font-semibold flex items-center gap-2 ${act.length ? "text-red-900" : "text-slate-700"}`}>
            <AlertTriangle className={`w-4 h-4 ${act.length ? "text-red-700" : "text-slate-400"}`} />
            {act.length ? `Guarantor insolvency — ${act.length} active` : "Guarantor insolvency (bankruptcy / IVA)"}
          </div>
          {act.length ? (
            <ul className="mt-1 space-y-1 text-xs text-red-900" data-testid="pg-insolvency-list">
              {act.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-2" data-testid={`pg-insolvency-row-${r.id}`}>
                  <span className="font-medium">{r.guarantor_name}</span> · {r.type_label}{r.case_reference ? <> · ref <span className="font-mono">{r.case_reference}</span></> : null} · {ukDate(r.date)}
                  {r.practitioner && <> · {r.practitioner}</>}{r.notes && <span className="text-red-800/80"> — {r.notes}</span>}
                  <span className="text-red-800/70">(logged by {r.recorded_by?.split("@")[0]} {ukDate(r.recorded_at)})</span>
                  <button type="button" className="underline text-[11px]" onClick={() => discharge(r)} data-testid={`pg-insolvency-discharge-${r.id}`}><CheckCircle2 className="inline w-3 h-3" /> discharged</button>
                  <button type="button" className="underline text-[11px]" onClick={() => remove(r)} data-testid={`pg-insolvency-remove-${r.id}`}><Trash2 className="inline w-3 h-3" /> remove</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500 mt-0.5">None logged. If a PG is made bankrupt or enters an IVA, log it here — letters and enforcement against them are then blocked.{past.length ? ` ${past.length} discharged record(s) on file.` : ""}</p>
          )}
          {act.length > 0 && <p className="text-[11px] text-red-800 mt-1">Letters wizard will refuse to address these guarantors unless the override is ticked. Pursue the company and any other guarantors as normal.</p>}
        </div>
        <Button size="sm" variant={act.length ? "outline" : "ghost"} className="shrink-0 h-7 text-xs" onClick={() => setOpen(true)} data-testid="pg-insolvency-log-btn"><Plus className="w-3 h-3 mr-1" /> Log bankruptcy / IVA</Button>
      </div>
      <LogDialog caseRow={caseRow} open={open} onOpenChange={setOpen} onSaved={onChanged} />
    </div>
  );
};
